const enableWsrvInDev = process.env.NEXT_PUBLIC_ENABLE_WSRV_IN_DEV === 'true';
const shouldUseWsrv = process.env.NODE_ENV === 'production' || enableWsrvInDev;

export const WSRV_COMPACT_WIDTHS = [80, 160, 240, 320, 480, 640, 960];
export const WSRV_HERO_WIDTHS = [768, 960, 1280, 1920];
export const WSRV_MATERIAL_WIDTHS = [768, 1280, 1920];
export const DEFAULT_WSRV_WIDTHS = WSRV_MATERIAL_WIDTHS;

export type WsrvImageOptions = {
  animated?: boolean;
  width?: number;
  height?: number;
  quality?: number;
  proxy?: boolean;
};

export type WsrvResponsiveImageSourceOptions = {
  animated?: boolean;
  src: string;
  widths: number[];
  quality?: number;
  proxy?: boolean;
};

export type WsrvResponsiveImageSource = {
  src: string;
  srcSet?: string;
};

function getConfiguredPublicBaseUrl() {
  return process.env.NEXT_PUBLIC_BASE_URL?.trim().replace(/\/$/, '') ?? '';
}

function isAbsoluteHttpUrl(src: string) {
  return src.startsWith('http://') || src.startsWith('https://');
}

function isRootRelativeUrl(src: string) {
  return src.startsWith('/') && !src.startsWith('//');
}

function resolveWsrvSourceUrl(src: string) {
  if (isAbsoluteHttpUrl(src)) {
    return src;
  }

  const publicBaseUrl = getConfiguredPublicBaseUrl();
  if (!publicBaseUrl) {
    return null;
  }

  const normalizedPath = isRootRelativeUrl(src) ? src : `/${src}`;
  return `${publicBaseUrl}${normalizedPath}`;
}

export const isWsrvEnabled = (src: string, proxy = true) => {
  if (!src) return false;
  if (src.startsWith('/workspace/') && !src.startsWith('/workspace/previews/'))
    return false;
  if (!proxy) return false;
  if (!shouldUseWsrv) return false;
  if (src.startsWith('data:') || src.startsWith('blob:')) return false;
  if (src.includes('wsrv.nl')) return false;
  try {
    const url = new URL(src);
    if (url.pathname.startsWith('/api/ai/jobs/')) return false;
  } catch {
    if (src.startsWith('/api/ai/jobs/')) return false;
  }
  return Boolean(resolveWsrvSourceUrl(src));
};

export const normalizeWsrvWidths = (widths?: number[]) => {
  if (!widths?.length) return [];
  const unique = Array.from(
    new Set(widths.filter((width) => Number.isFinite(width) && width > 0))
  );
  return unique.sort((a, b) => a - b);
};

export const buildWsrvUrl = (src: string, options?: WsrvImageOptions) => {
  const absoluteUrl = resolveWsrvSourceUrl(src);
  if (!absoluteUrl) {
    return src;
  }

  const params = new URLSearchParams();
  params.set('url', new URL(absoluteUrl).href);
  if (options?.animated) params.set('n', '-1');
  if (options?.width) params.set('w', String(Math.round(options.width)));
  if (options?.height) params.set('h', String(Math.round(options.height)));
  if (options?.quality) params.set('q', String(Math.round(options.quality)));
  return `https://wsrv.nl/?${params.toString()}`;
};

export function getWsrvImageUrl(src: string, options?: WsrvImageOptions) {
  if (!isWsrvEnabled(src, options?.proxy)) return src;
  return buildWsrvUrl(src, options);
}

export function getWsrvImageSrcSet(
  src: string,
  widths: number[],
  options?: Omit<WsrvImageOptions, 'width'>
) {
  if (!isWsrvEnabled(src, options?.proxy)) return undefined;
  const resolvedWidths = normalizeWsrvWidths(widths);
  if (!resolvedWidths.length) return undefined;
  return resolvedWidths
    .map((width) => `${buildWsrvUrl(src, { ...options, width })} ${width}w`)
    .join(', ');
}

export function resolveWsrvResponsiveImageSource({
  src,
  widths,
  quality,
  proxy,
  animated,
}: WsrvResponsiveImageSourceOptions): WsrvResponsiveImageSource {
  const normalizedSrc = src.trim();
  const resolvedWidths = normalizeWsrvWidths(widths);
  const srcSet =
    isWsrvEnabled(normalizedSrc, proxy) && resolvedWidths.length > 1
      ? getWsrvImageSrcSet(normalizedSrc, resolvedWidths, {
          quality,
          proxy,
          animated,
        })
      : undefined;

  return {
    src: isWsrvEnabled(normalizedSrc, proxy)
      ? resolvedWidths.length
        ? getWsrvImageUrl(normalizedSrc, {
            width: resolvedWidths[0],
            quality,
            proxy,
            animated,
          })
        : getWsrvImageUrl(normalizedSrc, { quality, proxy, animated })
      : normalizedSrc,
    srcSet,
  };
}
